import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "Draft");

export const UpdateArticle = ({ u }) => (
  <article className="space-y-4" data-testid={`update-article-${u.id}`}>
    <div>
      <p className="font-code text-[10px] uppercase tracking-[0.3em] text-amber-500/80">{fmt(u.published_at)}</p>
      <h3 className="font-display text-3xl text-amber-50 mt-1 leading-tight" data-testid={`update-title-${u.id}`}>{u.title}</h3>
    </div>
    {u.image_url && <img src={u.image_url} alt="" className="w-full max-h-[22rem] object-cover rounded-xl border border-line" data-testid={`update-image-${u.id}`} />}
    {u.html && <div className="wiki-prose text-sm" dangerouslySetInnerHTML={{ __html: u.html }} />}
  </article>
);

export const UpdatesDialog = ({ open, updates, onClose, preview }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-panel border-line max-w-2xl max-h-[88vh] overflow-y-auto p-0" data-testid="updates-dialog">
      <div className="px-7 pt-7 pb-2">
        <p className="flex items-center gap-2 font-code text-xs uppercase tracking-[0.3em] text-amber-400"><Sparkles className="w-4 h-4" />{preview ? "Preview" : "What's new"}</p>
        <DialogTitle className="sr-only">What's new in Lorely</DialogTitle>
        <DialogDescription className="text-sm text-slate-400 mt-2">
          {updates.length > 1 ? `${updates.length} updates since your last visit, newest first.` : "Here's what changed in Lorely."}
        </DialogDescription>
      </div>
      <div className="px-7 pb-4 divide-y divide-line [&>*]:py-6">
        {updates.map((u) => <UpdateArticle key={u.id} u={u} />)}
      </div>
      <div className="sticky bottom-0 flex justify-end border-t border-line bg-panel/95 backdrop-blur px-7 py-4">
        <button onClick={onClose} className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-sm font-semibold px-6 h-10" data-testid="updates-dismiss-btn">{preview ? "Close preview" : "Got it"}</button>
      </div>
    </DialogContent>
  </Dialog>
);

// Shows every published update the signed-in user hasn't seen yet, once per visit
export const UpdatesPopup = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!user) return setList([]);
    api.get("/updates/unseen").then((r) => setList(r.data)).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => {
    api.post("/updates/seen", { up_to: list[0].published_at }).catch(() => {});
    setList([]);
  };
  return list.length ? <UpdatesDialog open updates={list} onClose={close} /> : null;
};
